import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { MapWrapper } from '../../../v2/styles'
import { useTracking } from '../../../hooks/useTracking'
import {
  buildWidgetSrc,
  isNabdStatusMessage,
  isTerminalWidgetStatus,
  widgetOrigin,
} from '../../../lib/robot-delivery'

const WidgetFrame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background-color: #fff;
`

type Props = {
  widgetUrl: string
}

/**
 * Nabd's floor-plan widget (spec §10), in the slot the courier map normally
 * fills. `allow-scripts allow-same-origin` is exactly what the widget needs to
 * reach its own origin for the plan and the live position — nothing more.
 *
 * The widget posts `nabd.delivery.status` on every change (spec §10.3); we
 * accept only messages from the widget's own origin, feed them to the robot
 * card, and on a terminal status re-fetch the order once so the stepper moves
 * from real state changes rather than from the widget's word.
 */
export default function RobotWidget({ widgetUrl }: Props) {
  const { setRobotStatus, refreshOrder } = useTracking()
  const origin = useMemo(() => widgetOrigin(widgetUrl), [widgetUrl])
  const refreshedRef = useRef(false)

  useEffect(() => {
    if (!origin) {
      return
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || !isNabdStatusMessage(event.data)) {
        return
      }
      const { status, phase, floor, etaMinutes } = event.data
      setRobotStatus({ status, phase, floor, etaMinutes })
      if (isTerminalWidgetStatus(status) && !refreshedRef.current) {
        refreshedRef.current = true
        refreshOrder()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [origin, setRobotStatus, refreshOrder])

  if (!origin) {
    return null
  }

  return (
    <MapWrapper data-testid="robot-widget">
      <WidgetFrame
        src={buildWidgetSrc(widgetUrl)}
        title="Robot delivery"
        sandbox="allow-scripts allow-same-origin"
      />
    </MapWrapper>
  )
}
